import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  FormArray,
  FormControl,
  FormGroupDirective,
  UntypedFormArray,
  UntypedFormGroup,
} from '@angular/forms';

import { BehaviorSubject, Subject, combineLatest } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FormConfigService } from 'src/app/form-config/form-config.service';
import { CLIENT_TYPE, KNOWLEDGE_AND_EXPERIENCE_OPTIONS } from '../../model/consts/client-type.const';
import { DISTRIBUTION_STRATEGY } from '../../model/consts/distribution-strategy.const';
import { ESG_THEMATICS_TYPE } from '../../model/consts/esg-thematics-type.const';
import { FEE_TYPE } from '../../model/consts/fee-type.const';
import { FORM_OF_OFFER } from '../../model/consts/form-of-offer.const';
import { SALES_REGIONS } from '../../model/consts/sales-regions.const';
import { SECONDARY_MKT_QUOTATION_TYPE_ITEMS } from '../../model/consts/secondary-mkt-quotation-type.const';
import { YES_NO } from '../../model/consts/yes-no.const';
import {
  LegalFormEnum,
  ProductCaptureGroupsEnum,
  SalesEnum
} from '../../model/enums/product-capture.enums';
import { ContactInformationService } from '../../services/contact-information.service';
import { ProductCaptureService } from '../../services/product-capture.service';

@Component({
  selector: 'app-sales',
  templateUrl: './sales.component.html',
  styleUrls: ['./sales.component.scss'],
})
export class SalesComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() formGroupName: string;
  @Input() canUserEdit: boolean;
  @Output() emitAdd: EventEmitter<any> = new EventEmitter();
  @Output() emitDelete: EventEmitter<any> = new EventEmitter();
  @Output() emitAddTargetMarketRow: EventEmitter<any> = new EventEmitter();
  @Output() emitDeleteTargetMarketRow: EventEmitter<any> = new EventEmitter();
  @Output() emitAddContactInformationRow: EventEmitter<any> =
    new EventEmitter();
  @Output() emitDeleteContactInformationRow: EventEmitter<any> =
    new EventEmitter();
  @Output() dateRangeOut: EventEmitter<any> = new EventEmitter();

  YES_NO = YES_NO;
  clientTypeItems = CLIENT_TYPE;
  knowledgeAndExperienceItems = KNOWLEDGE_AND_EXPERIENCE_OPTIONS;
  currencyItems: Record<string, any>;
  esgThematicsItems = ESG_THEMATICS_TYPE;
  secondaryMktQuotationTypeItems = SECONDARY_MKT_QUOTATION_TYPE_ITEMS;
  distributionStrategyItems = DISTRIBUTION_STRATEGY;
  form: UntypedFormGroup;
  formOfOfferItems = FORM_OF_OFFER;
  labels: Record<string, any>;
  feeTypeItems = FEE_TYPE;
  salesRegionItems = SALES_REGIONS;
  allDeskItems: { id: string; displayValue: string; issuerId: string }[] = [];
  deskItems$: BehaviorSubject<
    { id: string; displayValue: string; issuerId: string }[]
  > = new BehaviorSubject([]);
  allContactItems$: BehaviorSubject<any[]> = new BehaviorSubject([]);
  PLEASE_SELECT_ISSUER_MESSAGE = 'No desks found. Please select an issuer.';
  NO_DESK_FOUND_MESSAGE = 'No desks found for the selected issuer.';
  noDesksMessage = this.PLEASE_SELECT_ISSUER_MESSAGE;
  contactInfoItems = [
    { id: 'Hedge Trader', displayValue: 'Hedge Trader' },
    { id: 'Secondary Market Trader', displayValue: 'Secondary Market Trader' },
    { id: 'Not provided', displayValue: 'Not provided' },
  ]

  private destroy$ = new Subject<void>();

  constructor(
    private productCaptureService: ProductCaptureService,
    public rootFormGroup: FormGroupDirective,
    private formConfigService: FormConfigService,
    private contactInformationService: ContactInformationService,
    private cd: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.getCurrencies();
    this.labels = this.formConfigService.getLabels(ProductCaptureGroupsEnum.sales);
    this.form = this.rootFormGroup.control.get(
      this.formGroupName
    ) as UntypedFormGroup;

    const issuerEntity = this.rootFormGroup.control.get([
      ProductCaptureGroupsEnum.legalForm,
      LegalFormEnum.legal,
      'issuerEntityId',
    ]);

    combineLatest([
      this.contactInformationService.getDesks(),
      issuerEntity.valueChanges,
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([desks, issuerId]) => {
        this.allDeskItems = desks.map((desk) => {
          return {
            id: desk.id.toString(),
            displayValue: desk.name,
            issuerId: desk.issuerId.toString(),
          };
        });

        this.updateDeskItemsAndNoDesksMessage(issuerId, this.allDeskItems);
      });

    this.contactInformationService
      .getContacts()
      .pipe(takeUntil(this.destroy$))
      .subscribe((contacts) => {
        this.allContactItems$.next(
          contacts.map((contact) => {
            return {
              id: contact.id.toString(),
              displayValue: contact.name,
              deskId: contact.deskId.toString(),
              name: contact.name,
              email: contact.email,
              location: contact.location,
            };
          })
        );
      });

    const fees = this.form.get('feesGroup');
    const feesArr = fees.get('feesArr');
    const targetMarkets = this.form.get('targetMarkets');

    feesArr.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      if (!value.length) {
        this.addRow();
      }
    });

    targetMarkets.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        if (!value.length) {
          this.addTargetMarketRow();
        }
      });
  }

  ngAfterViewInit(): void {
    combineLatest([
      this.allContactItems$,
      this.getContactInformationRows.valueChanges,
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([_, value]) => {
        if (!value.length) {
          this.addContactInformationRow();

          return;
        }

        value.forEach((contact, index) => {
          this.onContactSelected(contact.contactId, index);
        });
      });

    this.cd.detectChanges();
  }

  get targetMarket(): UntypedFormGroup {
    return this.form.get(SalesEnum.targetMarket) as UntypedFormGroup;
  }

  get getSalesFeesGroup(): UntypedFormGroup {
    return this.form.get(SalesEnum.feesGroup) as UntypedFormGroup;
  }

  get getFeesRow(): UntypedFormArray {
    return this.getSalesFeesGroup.get('feesArr') as UntypedFormArray;
  }

  get getTargetMarketRows(): UntypedFormArray {
    return this.form.get(SalesEnum.targetMarkets) as UntypedFormArray;
  }

  get getContactInformationRows(): UntypedFormArray {
    return this.form.get(SalesEnum.contactInformation) as UntypedFormArray;
  }

  get countriesOfOffer(): FormArray {
    return this.rootFormGroup.control.get(ProductCaptureGroupsEnum.legalForm)
      .get(LegalFormEnum.countriesOfOffer)
      .get(LegalFormEnum.countriesOfOfferArr) as FormArray;
  }

  get secondaryMarketAndDataPublising(): UntypedFormGroup {
    return this.form.get(SalesEnum.secondaryMarketAndDataPublising) as UntypedFormGroup;
  }

  get telekursSetUp(): FormControl {
    return this.secondaryMarketAndDataPublising.get(SalesEnum.salesTelekursSetUp) as FormControl;
  }

  get secondaryMktQuotationType(): FormControl {
    return this.form.get(SalesEnum.salesSecondaryMktQuotationType) as FormControl;
  }

  get secondaryMktRequired(): FormControl {
    return this.secondaryMarketAndDataPublising.get(SalesEnum.salesSecondaryMktRequired) as FormControl;
  }

  addRow(): void {
    this.emitAdd.emit();
  }

  deleteRow(index): void {
    this.emitDelete.emit(index);
  }

  addTargetMarketRow(): void {
    this.emitAddTargetMarketRow.emit();
  }

  deleteTargetMarketRow(index): void {
    this.emitDeleteTargetMarketRow.emit(index);
  }

  addContactInformationRow(): void {
    this.emitAddContactInformationRow.emit();
  }

  deleteContactInformationRow(index): void {
    this.emitDeleteContactInformationRow.emit(index);
  }

  scheduleOutput(obj): void {
    this.dateRangeOut.emit(obj);
  }

  onDeskSelected(index: number): void {
    if (this.getContactInformationRows.at(index).value.contactId !== null) {
      this.getContactInformationRows.at(index).patchValue({
        contactId: null,
        email: null,
        location: null,
        contactInformation: null,
      });
    }
    this.onContactSelected(null, index);
  }

  onContactSelected(contactId: string, index: number): void {
    const contact = this.allContactItems$.value?.find(
      (contact) => contact.id === contactId
    );

    if (!contact) {
      return;
    }

    const contactRow = this.getContactInformationRows.at(index);
    if (
      contactRow.get('email').value !== contact?.email ||
      contactRow.get('location').value !== contact?.location
    ) {
      contactRow.patchValue({
        email: contact?.email || null,
        location: contact?.location || null,
      });
    }
  }

  private getCurrencies(): void {
    this.productCaptureService
      .getCurrencies()
      .pipe(takeUntil(this.destroy$))
      .subscribe((currencies) => {
        if (currencies?.length > 0 && !this.currencyItems?.length) {
          this.currencyItems = currencies.map((currency) => {
            return {
              id: currency.shortName,
              displayValue: currency.shortName,
            };
          });
        }
      });
  }

  private updateDeskItemsAndNoDesksMessage(
    issuerId: number,
    allDeskItems: any[]
  ): void {
    if (!issuerId) {
      this.deskItems$.next([]);
      this.noDesksMessage = this.PLEASE_SELECT_ISSUER_MESSAGE;
      this.getContactInformationRows.clear();

      return;
    }

    const desksForIssuer = allDeskItems.filter(
      (desk) => desk.issuerId === issuerId.toString()
    );
    this.deskItems$.next(desksForIssuer);
    if (!desksForIssuer.length) {
      this.noDesksMessage = this.NO_DESK_FOUND_MESSAGE;
      this.getContactInformationRows.clear();
    } else {
      this.noDesksMessage = '';
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
 